import { useEffect, useState } from "react";
import { Col, Row } from "antd";
import { FormBox, InputBox } from "../../../../../components/AntdAddons";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../utils/hooks/hooks";
import {
  fetchEducationTrainingList,
  fetchExperienceList,
  fetchEducationLevelsList

} from "../../../../../store/CommonSlice";
import { rules } from "./rules";
import { fetchIndustryWizeData } from "../../../Industry/utils/slice";
import { fetchIndustryList } from "../../../Industry/utils/slice";
import { INDUSTRY_FILTER } from "../../../../../config/Constant";
import React from "react";


/**
 * Renders a form component for creating or editing a career path.
 *
 * @param {object} props - The props for the FormComponent.
 * @param {object} props.form - The form instance from antd Form.
 * @param {string} props.id - The id of the form.
 * @param {function} props.handleSubmit - The function to handle form submission.
 * @param {function} props.onValuesChange - The function to handle form field value changes.
 * @param {object} props.editValues - The values for editing a career path.
 * @return {JSX.Element} The rendered FormComponent.
 */
const FormComponent: React.FC<any> = ({
  form,
  id,
  handleSubmit,
  onValuesChange,
  editValues,
}) => {
  const educationTrainingList = useAppSelector(
    (state) => state.common.educationTrainingList
  );

  const initialAppOption: any = useAppSelector(
    (state) => state.auth.initialData
  );

  const experienceList = useAppSelector((state) => state.common.experienceList);
  const educationLevelList = useAppSelector((state) => state.common.educationLevelsList);

  const dispatch = useAppDispatch();

  const [industryList, setIndustryList] = useState<any>([]);
  const [careersList, setCareersList] = useState<any>([]);

  useEffect(() => {

    // api call when component mounts
    dispatch(fetchEducationTrainingList()).catch((error: any) => error);
    dispatch(fetchExperienceList()).catch((error: any) => error);
    dispatch(fetchEducationLevelsList()).catch((error: any) => error);


    dispatch(fetchIndustryList())
      .then((data: any) => {
        setIndustryList(data.rows);
      })
      .catch((error: any) => error);
  }, []);

  useEffect(() => {
    if (editValues) {
      const careerIds = editValues?.careers?.map((data: any) => {
        return data.id;
      });
      const careerLevel: any = initialAppOption?.career_path_levels?.find(
        (data: any) => data.name === editValues?.career_level
      )?.id;
      form.setFieldsValue({
        career_level: careerLevel,
        code: editValues?.code,
        career_ids: careerIds,
        education_training_id: editValues?.education_training_id,
        experience_id: editValues?.experience_id,
        industry_id: editValues?.industry_id,
        education_level_id: editValues?.education_level_id,
        experience_level_id: editValues?.experience_level_id,
      });
      onIndustryChange(editValues.industry_id, false);
    }
  }, [editValues, form]);

  const onFinish = (data: any) => {
    handleSubmit(data);
  };


  const onIndustryChange = (id: number, resetVal = true) => {
    resetVal && form.setFieldsValue({ career_ids: null });
    setCareersList([]);
    dispatch(fetchIndustryWizeData([id], [INDUSTRY_FILTER.CAREER]))
      .then((response: any) => {
        setCareersList(response.data.career);
      })
      .catch((error: any) => error);
  }

  return (
    <>
      <FormBox
        form={form}
        id={id}
        onFinish={onFinish}
        onValuesChange={onValuesChange}
      >
        <Row gutter={15}>
          <Col xs={{ span: 24 }} md={{ span: 12 }}>
            <InputBox.Text
              required
              name="code"
              label="Code"
              rules={rules.code()}
            />
          </Col>
          <Col xs={{ span: 24 }} md={{ span: 12 }}>
            <InputBox.Select
              required
              name="career_level"
              label="Career Level"
              className="singleSelect"
              showSearch
              optionFilterProp="children"
              filterOption={(input, option: any) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              options={{
                list: initialAppOption?.career_path_levels,
                valueKey: "id",
                textKey: "name",
              }}
            />
          </Col>
          <Col xs={{ span: 24 }} md={{ span: 12 }} >
            <InputBox.Select
              required
              label="Industry Name"
              name="industry_id"
              showSearch
              className="singleSelect"
              optionFilterProp="children"
              filterOption={(input, option: any) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                0
              }
              onChange={(val) => onIndustryChange(val)}
              options={{
                list: industryList,
                valueKey: "id",
                textKey: "industry_name",
              }}
            />

          </Col>
          <Col xs={{ span: 24 }} md={{ span: 12 }}>
            <InputBox.Select
              required
              name="career_ids"
              label="Careers"
              showSearch
              optionFilterProp="children"
              filterOption={(input, option: any) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              options={{
                list: careersList,
                valueKey: "id",
                textKey: "name",
              }}
              mode="multiple"
            />
          </Col>
          <Col xs={{ span: 24 }} md={{ span: 12 }}>
            <InputBox.Select
              required
              name="education_training_id"
              label="Education Training"
              showSearch
              className="singleSelect"
              optionFilterProp="children"
              filterOption={(input, option: any) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              options={{
                list: educationTrainingList,
                valueKey: "id",
                textKey: "name",
              }}
            />
          </Col>
          <Col xs={{ span: 24 }} md={{ span: 12 }}>
            <InputBox.Select
              required
              name="education_level_id"
              label="Education Level"
              showSearch
              className="singleSelect"
              optionFilterProp="children"
              filterOption={(input, option: any) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              options={{
                list: initialAppOption?.profile_education_level,
                valueKey: "id",
                textKey: "name",
              }}
            />
          </Col>
          <Col xs={{ span: 24 }} md={{ span: 12 }}>
            <InputBox.Select
              required
              name="experience_id"
              label="Experience"
              showSearch
              className="singleSelect"
              optionFilterProp="children"
              filterOption={(input, option: any) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              options={{
                list: experienceList,
                valueKey: "id",
                textKey: "name",
              }}
            />
          </Col>
          <Col xs={{ span: 24 }} md={{ span: 12 }}>
            <InputBox.Select
              required
              name="experience_level_id"
              label="Experience Level"
              showSearch
              className="singleSelect"
              optionFilterProp="children"
              filterOption={(input, option: any) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              options={{
                list: initialAppOption?.profile_year_of_experience,
                valueKey: "id",
                textKey: "name",
              }}
            />
          </Col>
        </Row>
      </FormBox>
    </>
  );
};

export default FormComponent;
